import { FC, useContext, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Button, Dropdown, Switch, Tooltip } from 'antd';
import tw from 'twin.macro';
import { UseMutateFunction, useQuery } from '@tanstack/react-query';
import { ReloadOutlined, ExportOutlined } from '@ant-design/icons';
import axios, { AxiosResponse } from 'axios';
import { pricingSimulatorDataContext } from 'contexts/PricingSimulatorContext';
import useUpdateDelistOrCompFollowStatus from 'hooks/pricingSimulator/scenario/useUpdateDelistOrCompFollowStatus';
import SearchBox from '../SearchBox';
import ScenarioFilter from './ScenarioFilter/ScenarioFilter';
import { compareUsers, generateErrorMsg, getConfig, sendNotification } from 'utils/helperMethods';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { pricingSimulatorUrls } from 'config/urls';
import { BulkUploadInfo, Project, Scenario } from 'types/pricingSimulator';
import { ConfigKeys, PricingSimulatorNotificationMessages } from 'utils/Enums';
import DeleteConfirmationModal from './Modals/DeleteConfirmationModal';
import { queryClient } from 'services/reactQueryService';
import { PricingSimulatorQueryEnum } from 'utils/queryKeysEnum';
import { errorStatuses, pollingStatuses } from 'components/organism/PricingSimulator/UpdateOrViewPricingSimulator';
import { transformFilterObjectWithDimensionsInfo } from 'utils/getPricingSimulatorTableColumns';
import { UserContext } from 'contexts/ConfigContext';

import ExportScenarioModal from './Modals/ExportScenarioModal';
import dayjs from 'dayjs';
import ResetConfirmationModal from './Modals/ResetConfirmationModal';
import { pricingSimulatorWrapperDataContext } from 'contexts/PricingSimulatorWrapperContext';
import DownloadTemplateModal from './Modals/DownloadTemplateModal';
import BulkUploadModal from './Modals/BulkUploadModal';
const CustomButton = styled(Button)`
    ${tw`text-purpleFont border border-purpleFont rounded-md`}
`;

export const CustomDropdownButton = styled(Dropdown.Button)`
    .ant-btn-compact-item.ant-btn.ant-btn-compact-first-item:not(.ant-btn-compact-last-item):not(
            .ant-btn-compact-item-rtl
        ) {
        padding: 0 !important;
    }
    .ant-btn-icon-only.ant-btn-compact-item {
        border-radius: 6px !important;
        overflow: hidden;
    }
`;

const delete_scenario_key = 'delete_scenario';
const conf_table_view_key = 'conf_table_view';
const reset_scenario = 'reset_scenario';
const duplicate_scenario = 'duplicate_scenario';
const download_template_key = 'download_template';
const upload_file_key = 'upload_file';

export enum UploadPollingStatus {
    IN_PROGRESS = 'IN_PROGRESS',
    ERROR = 'ERROR',
    SUCCESS = 'SUCCESS',
    NULL = '',
}

export const UPLOAD_POLLING_INTERVAL = 10000;

type Props = {
    simulateScenarioMutation: UseMutateFunction<AxiosResponse<any, any> | undefined, Error, any, unknown>;
    resetScenarioMutation: UseMutateFunction<AxiosResponse<any, any> | undefined, Error, any, unknown>;
    onDuplicateScenarioClick: Function;
    commonLoader: boolean;
};

const ScenarioActions: FC<Props> = ({
    simulateScenarioMutation,
    commonLoader,
    resetScenarioMutation,
    onDuplicateScenarioClick,
}) => {
    const { appConfig } = useContext(UserContext);
    const pricingSimulatorConfig = getConfig(ConfigKeys.PRICING_SIMULATOR, appConfig);
    const hideBulkUploadButton = !pricingSimulatorConfig?.enableBulkUpload;

    const disableExport = pricingSimulatorConfig?.disableExport;
    const {
        dimensionCheckboxObj,
        setDimensionCheckboxObj,
        refetchScenarioTableData,
        selectedScenarioInfo,
        setIsScenarioDeleted,
        selectedProjectInfo,
        setSelectedProjectInfo,
        updateScenarioProperty,
        viewMode,
        isDropdownAtLowestLevel,
        applyFilterObject,
        selectedDimensionListValues,
        setActivateExportPolling,
        showPercentageChange,
        setShowPercentageChange,
        defaultScenarioTableColumnsLength,
        searchKey,
        setSearchKey,
        availableFilters,
        allCheckedAttribute,
        setAllCheckedAttribute,
        allSelectedValueForAllAttribute,
        setAllSelectedValueForAllAttribute,
        setApplyFilterObject,
        resetFilterObject,
        showIsEditedRecords,
        setShowIsEditedRecords,
        saveFilterConfig,
    } = useContext(pricingSimulatorDataContext);

    const { dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);
    const scenarioFilterKeys = dimensionConfigData?.scenarioFilterKeys ?? [];

    const [openDeletePopup, setOpenDeletePopup] = useState(false);
    const [openResetPopup, setOpenResetPopup] = useState(false);
    const [openExportPopup, setOpenExportPopup] = useState(false);
    const [openDownlaodTemplatePopup, setOpenDownlaodTemplatePopup] = useState(false);
    const [openBulkUploadModal, setOpenBulkUploadModal] = useState(false);
    const [pollingStatus, setPollingStatus] = useState<null | UploadPollingStatus>(null);

    const { row_ids, showCompFollowBtn, showUndoCompFollowBtn, showDelistBtn, showUndoDelistBtn } =
        getDelistAndCompActionsInfo(dimensionCheckboxObj, isDropdownAtLowestLevel);

    const { mutate: triggerDelistOrCompFollowUpdate, loading: delistOrCompFollowLoading } =
        useUpdateDelistOrCompFollowStatus({ refetchScenarioTableData, setDimensionCheckboxObj });

    const baseDelistOrCompFollowPayload = useMemo(() => {
        const filters = transformFilterObjectWithDimensionsInfo(applyFilterObject, scenarioFilterKeys);

        return {
            scenario_id: selectedScenarioInfo?.scenarioId,
            search_keyword: '',
            dimension_attributes: selectedDimensionListValues,
            row_ids: row_ids,
            filters: filters,
            user_name: localStorage.getItem('email'),
        };
    }, [dimensionCheckboxObj, scenarioFilterKeys]);

    const delistOrCompFollowActionClick = (column: 'comp_follow' | 'delisted', value: boolean) => {
        const finalPayload = { ...baseDelistOrCompFollowPayload, column, value };
        triggerDelistOrCompFollowUpdate(finalPayload);
    };

    //called when clicked on search icon or enter
    const onSearch = (val: string) => {
        updateScenarioProperty('search', val);
        updateScenarioProperty('offset', 0);
    };

    //syncing context search key with component
    useEffect(() => {
        if (selectedScenarioInfo?.search !== searchKey) setSearchKey(selectedScenarioInfo?.search || '');
    }, [selectedScenarioInfo?.search]);

    const isSameUser = useMemo(() => {
        return Boolean(compareUsers(selectedScenarioInfo?.createdBy || ''));
    }, [selectedScenarioInfo?.createdBy]);

    const { mutate: deleteScenario, loading: deleteScenarioLoading } = useCommonMutationHandler({
        url: pricingSimulatorUrls.deleteScenarioUrl,
        method: 'delete',
    });

    const onDeleteConfirmation = () => {
        deleteScenario(
            { scenario_id: selectedScenarioInfo?.scenarioId, user_name: localStorage.getItem('email') },
            {
                onSuccess: (data: any) => {
                    //on success, filter out the deleted scenario and update the list
                    const newScenarios = selectedProjectInfo?.simulationList?.filter((item: Scenario) => {
                        return item?.scenarioId !== selectedScenarioInfo?.scenarioId;
                    });
                    sendNotification('success', PricingSimulatorNotificationMessages.DELETE_SCENARIO_SUCCESS);
                    setSelectedProjectInfo({
                        ...selectedProjectInfo,
                        simulationList: newScenarios,
                    });
                    setOpenDeletePopup(false);
                    setIsScenarioDeleted(true);
                },
                onError: (val: any) => {
                    sendNotification('error', PricingSimulatorNotificationMessages.GENERALIZED_ERROR_MSG);
                    setOpenDeletePopup(false);
                },
            },
        );
    };

    function resetScenario(payload: any) {
        if (disableResetButton) return;
        const filters = transformFilterObjectWithDimensionsInfo(applyFilterObject, scenarioFilterKeys);

        resetScenarioMutation(
            {
                ...payload,
            },
            {
                onSuccess: (data) => {
                    const updatedAtText = dayjs.unix(data?.data?.updated_at).fromNow();
                    sendNotification('success', PricingSimulatorNotificationMessages.RESET_SCENARIO_SUCCESS);
                    updateScenarioProperty('status', data?.data?.status);
                    updateScenarioProperty('updatedAt', updatedAtText);
                    // ui reset upload status
                    const bulkUploadInfo: BulkUploadInfo = { errorObject: {}, status: undefined };
                    updateScenarioProperty('bulkUploadInfo', bulkUploadInfo);
                    //updating status in projects list
                    setSelectedProjectInfo((prevScenarioInfo: Project | null) => {
                        let tempScenarioInfo: any = prevScenarioInfo || {};
                        const newScenarios = tempScenarioInfo?.simulationList?.map((item: Scenario) => {
                            return {
                                ...item,
                                status:
                                    selectedScenarioInfo?.scenarioId === item?.scenarioId
                                        ? data?.data?.status
                                        : item?.status,
                                updatedAt:
                                    selectedScenarioInfo?.scenarioId === item?.scenarioId
                                        ? updatedAtText
                                        : item?.updatedAt,
                            };
                        });
                        const parsedData = {
                            ...tempScenarioInfo,
                            simulationList: newScenarios || [],
                        };
                        return parsedData;
                    });
                    setOpenResetPopup(false);
                    refetchScenarioTableData();
                    setDimensionCheckboxObj({});
                },
                onError: (error: any) => {
                    const errorMsg = generateErrorMsg(
                        String(error?.response?.data?.detail),
                        PricingSimulatorNotificationMessages.GENERALIZED_ERROR_MSG,
                    );
                    setOpenResetPopup(false);
                    //show error message
                    sendNotification('error', errorMsg);
                },
            },
        );
    }

    function simulateScenario() {
        if (disableSimulateButton) return;

        simulateScenarioMutation(
            { scenario_id: selectedScenarioInfo?.scenarioId },
            {
                onSuccess: (data) => {
                    sendNotification('info', PricingSimulatorNotificationMessages.SIMULATE_SCENARIO_SUCCESS);
                    updateScenarioProperty('status', data?.data?.status);
                    // refetch scenario status query
                    queryClient.refetchQueries({
                        exact: false,
                        queryKey: [PricingSimulatorQueryEnum.SCENARIO_STATUS, String(selectedScenarioInfo?.scenarioId)],
                    });
                    // TODO: status gets updated which triggers the fetch data call so commenting out for now, will remove later
                    // once simulation is triggered we need to clear all the lens data calls
                    // queryClient.refetchQueries({
                    //     exact: false,
                    //     queryKey: [PricingSimulatorQueryEnum.SCENARIO_LENS_DATA],
                    // });
                },
                onError: (val: any) => {
                    sendNotification('error', PricingSimulatorNotificationMessages.SIMULATE_SCENARIO_ERROR);
                    setOpenDeletePopup(false);
                },
            },
        );
    }
    const exportClickHandler = () => {
        if (disableSimulateButton) return;
        setOpenExportPopup(true);
    };

    const disableSimulateButton = useMemo(() => {
        const hasError = selectedScenarioInfo?.error;
        const hasErrorStatus = errorStatuses.includes(selectedScenarioInfo?.status || '');
        const isPolling = pollingStatuses.includes(selectedScenarioInfo?.status || '');
        const isBulkUploadInProgress = selectedScenarioInfo?.bulkUploadInfo?.status === 'IN_PROGRESS';
        const isDataTooShort =
            Object.keys(selectedScenarioInfo?.data?.[0] || {}).length <= defaultScenarioTableColumnsLength;

        return commonLoader || hasError || hasErrorStatus || isPolling || isBulkUploadInProgress || isDataTooShort;
    }, [commonLoader, selectedScenarioInfo, errorStatuses, pollingStatuses, defaultScenarioTableColumnsLength]);

    const disableResetButton = useMemo(() => {
        return (
            disableSimulateButton ||
            selectedScenarioInfo?.bulkUploadInfo?.status === 'IN_PROGRESS' ||
            !compareUsers(selectedScenarioInfo?.createdBy || '')
        );
    }, [disableSimulateButton, selectedScenarioInfo?.bulkUploadInfo?.status]);

    const disableDeleteButton = useMemo(() => {
        return (
            selectedScenarioInfo?.bulkUploadInfo?.status === 'IN_PROGRESS' ||
            !isSameUser ||
            selectedProjectInfo?.simulationList?.length == 1
        );
    }, [selectedScenarioInfo, isSameUser]);

    const disableBulkUploadButton = useMemo(() => {
        return selectedScenarioInfo?.bulkUploadInfo?.status === 'IN_PROGRESS' || !isSameUser;
    }, [selectedScenarioInfo, isSameUser]);

    const disableSearchFilterButton = commonLoader || pollingStatuses.includes(selectedScenarioInfo?.status || '');

    const onFilterSaveClickHandler = (resultObj: any) => {
        setAllCheckedAttribute(resultObj.allCheckedAttribute);
        setAllSelectedValueForAllAttribute(resultObj.allSelectedValueForAllAttribute);
        setShowIsEditedRecords(resultObj?.showIsEditedRecords);
        setApplyFilterObject(resultObj.applyFilterObject);
        saveFilterConfig(resultObj);
    };

    const pollingFn = async () => {
        try {
            const data = await axios.get(`${pricingSimulatorUrls.getUploadStatus}/${selectedScenarioInfo?.scenarioId}`);
            setPollingStatus(data.data.bulk_upload_status);
            const newBulkUploadInfo: BulkUploadInfo = {
                status: data.data?.bulk_upload_status,
                errorObject: data?.data?.upload_error_message,
            };
            if (data.data?.bulk_upload_status === UploadPollingStatus.SUCCESS) refetchScenarioTableData();
            updateScenarioProperty('bulkUploadInfo', newBulkUploadInfo);
        } catch (err: any) {
            const newBulkUploadInfo: BulkUploadInfo = {
                status: UploadPollingStatus.ERROR,
                errorObject: {},
            };
            setPollingStatus(UploadPollingStatus.ERROR);
            updateScenarioProperty('bulkUploadInfo', newBulkUploadInfo);
            console.error(err);
            return err.message;
        }
    };

    const dropdownItems: any = [
        {
            disabled: disableResetButton,
            label: (
                <div className={`font-xs ${disableResetButton ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                    Reset Scenario
                </div>
            ),
            key: reset_scenario,
        },
        {
            disabled: disableSearchFilterButton,
            label: (
                <div className={`font-xs ${disableSearchFilterButton ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                    Duplicate Scenario
                </div>
            ),
            key: duplicate_scenario,
        },
        !hideBulkUploadButton && {
            disabled: disableBulkUploadButton,
            label: <div className="font-xs cursor-pointer">Bulk Update</div>,
            key: 'bulk_update',
            children: [
                {
                    label: <div className="font-xs cursor-pointer">Download Template</div>,
                    key: download_template_key,
                },
                {
                    label: <div className="font-xs cursor-pointer">Upload File</div>,
                    key: upload_file_key,
                },
            ],
        },
        {
            type: 'divider',
        },
        {
            disabled: disableDeleteButton,
            label: (
                <Tooltip
                    placement="left"
                    title={
                        selectedProjectInfo?.simulationList?.length === 1
                            ? PricingSimulatorNotificationMessages.MIN_ONE_SCENARIO_FOR_PROJECT
                            : ''
                    }
                >
                    <label
                        className={`font-xs ${
                            disableDeleteButton ? 'cursor-not-allowed' : 'cursor-pointer text-redAlt'
                        }`}
                    >
                        Delete Scenario
                    </label>
                </Tooltip>
            ),
            key: delete_scenario_key,
        },
    ].filter(Boolean); // Removes false/null items

    const { isLoading } = useQuery({
        queryKey: [`get_upload_status/${selectedScenarioInfo?.scenarioId}`],
        queryFn: pollingFn,
        refetchInterval: pollingStatus === UploadPollingStatus.IN_PROGRESS ? UPLOAD_POLLING_INTERVAL : false, // Start polling only if `polling` is true
        enabled: !!selectedScenarioInfo?.scenarioId, // Ensure query only runs if `polling` is true
        staleTime: 0,
    });

    return (
        <div className={`flex items-center gap-2 ${delistOrCompFollowLoading ? 'pointer-events-none ' : ''}`}>
            {Object.keys(dimensionCheckboxObj || {}).length > 0 && !viewMode && (
                <>
                    {showCompFollowBtn && (
                        <CustomButton onClick={() => delistOrCompFollowActionClick('comp_follow', true)}>
                            Competitors to Follow
                        </CustomButton>
                    )}
                    {showUndoCompFollowBtn && (
                        <CustomButton onClick={() => delistOrCompFollowActionClick('comp_follow', false)}>
                            Undo Competitors to Follow
                        </CustomButton>
                    )}
                    {showDelistBtn && (
                        <CustomButton onClick={() => delistOrCompFollowActionClick('delisted', true)}>
                            Delist
                        </CustomButton>
                    )}
                    {showUndoDelistBtn && (
                        <CustomButton onClick={() => delistOrCompFollowActionClick('delisted', false)}>
                            Undo Delist
                        </CustomButton>
                    )}
                </>
            )}

            {/* TODO: for v2 */}
            <SearchBox
                disabled={disableSearchFilterButton}
                onSearch={onSearch}
                search={searchKey}
                setSearch={setSearchKey}
            />
            <div className="flex gap-1 items-center">
                <span className="text-promoColour font-xxs">Abs</span>
                <Switch checked={showPercentageChange} onChange={(checked) => setShowPercentageChange(checked)} />
                <span className="text-promoColour font-xxs">%</span>
            </div>
            <ScenarioFilter
                disabled={disableSearchFilterButton}
                availableFilters={availableFilters}
                allCheckedAttribute={allCheckedAttribute}
                allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                applyFilterObject={applyFilterObject}
                resetFilterObject={resetFilterObject}
                showIsEditedRecords={showIsEditedRecords}
                setShowIsEditedRecords={setShowIsEditedRecords}
                scenarioId={selectedScenarioInfo?.scenarioId || 0}
                onSubmitHandler={onFilterSaveClickHandler}
            />
            <Button
                onClick={exportClickHandler}
                disabled={disableSimulateButton || disableExport}
                style={{ borderRadius: 6 }}
                type="default"
                icon={<ExportOutlined />}
            >
                Export
            </Button>
            <div
                onClick={simulateScenario}
                style={{ minWidth: '100px' }}
                className={`rounded  py-1 px-2 border  text-center flex justify-around items-center   ${
                    disableSimulateButton
                        ? 'cursor-not-allowed border-borderGrey bg-backgroundGreyAlt6  text-promoColour'
                        : 'cursor-pointer border-purpleFont  bg-purpleFont text-white'
                }`}
            >
                <ReloadOutlined /> <span>Simulate</span>
            </div>

            <CustomDropdownButton
                trigger={['click']}
                menu={{
                    onClick: (val) => {
                        switch (val.key) {
                            case delete_scenario_key:
                                if (!isSameUser) return;
                                if ((selectedProjectInfo?.simulationList?.length || 0) < 2) return;
                                setOpenDeletePopup(true);
                                return;

                            case reset_scenario:
                                if (disableResetButton) return;
                                setOpenResetPopup(true);
                                return;
                            case duplicate_scenario:
                                if (disableSearchFilterButton) return;
                                onDuplicateScenarioClick();
                                return;
                            case download_template_key:
                                setOpenDownlaodTemplatePopup(true);
                                return;
                            case upload_file_key:
                                setOpenBulkUploadModal(true);
                                return;
                            default:
                                return;
                        }
                    },
                    style: { borderRadius: 8, padding: 4 },
                    items: dropdownItems,
                }}
            ></CustomDropdownButton>
            <DeleteConfirmationModal
                loading={deleteScenarioLoading}
                onSubmitClick={onDeleteConfirmation}
                onCancel={(e) => {
                    setOpenDeletePopup(false);
                }}
                open={openDeletePopup}
            />
            <ResetConfirmationModal
                loading={commonLoader}
                onSubmitClick={resetScenario}
                onCancel={(e) => {
                    setOpenResetPopup(false);
                }}
                open={openResetPopup}
            />
            <ExportScenarioModal
                onSubmitClick={(taskId: string, enablePolling: boolean) => {
                    setActivateExportPolling({ taskId, enablePolling });
                    setOpenExportPopup(false);
                }}
                onCancel={(e) => {
                    setOpenExportPopup(false);
                }}
                open={openExportPopup}
            />
            <DownloadTemplateModal
                open={openDownlaodTemplatePopup}
                onCancel={() => setOpenDownlaodTemplatePopup(false)}
                onSubmitFinish={() => setOpenDownlaodTemplatePopup(false)}
            />
            <BulkUploadModal
                open={openBulkUploadModal}
                onCancel={() => setOpenBulkUploadModal(false)}
                onSubmitFinish={() => setOpenBulkUploadModal(false)}
                setPollingStatus={setPollingStatus}
            />
        </div>
    );
};

export default ScenarioActions;

const getDelistAndCompActionsInfo = (dimensionCheckboxObj: any, isDropdownAtLowestLevel: boolean) => {
    let showCompFollowBtn = false;
    let showUndoCompFollowBtn = true;
    let showDelistBtn = false;
    let showUndoDelistBtn = true;
    let row_ids: Array<number> = [];
    let isAllSelected = false;

    Object.keys(dimensionCheckboxObj).forEach((key: string, index: number) => {
        if (key === 'selectAll') {
            isAllSelected = true;
        } else {
            const obj = dimensionCheckboxObj[key];

            if (obj.pk) {
                row_ids.push(obj.pk);
            }

            if (!obj.isCompetitorToFollow) {
                showCompFollowBtn = isDropdownAtLowestLevel ? true : false;
                showUndoCompFollowBtn = false;
            }
            if (!obj.isDeListed) {
                showDelistBtn = isDropdownAtLowestLevel ? true : false;
                showUndoDelistBtn = false;
            }
        }
    });

    // if all is selected keeping row_ids empty, BE will be handled
    if (isAllSelected) {
        row_ids = [];
    }

    return { row_ids, showCompFollowBtn, showUndoCompFollowBtn, showDelistBtn, showUndoDelistBtn };
};
